/**
 * Section: Tw incident map
 * -----------------------------------------------------------------------------
 * Opens an incident's dialog from its map marker or list button, closes it
 * on a backdrop click, and returns focus to the control that opened it.
 * The dialogs close natively with Escape and their own close button.
 * Wrapped in a guard so a second copy of the section doesn't redefine it.
 *
 */

if (!customElements.get('tw-incident-map')) {

  /**
   * Custom element wrapping the section, its triggers, and dialogs.
   */
  class TwIncidentMap extends HTMLElement {

    /**
     * Bind listeners.
     */
    connectedCallback() {
      this.opener = null
      this.dialogs = [...this.querySelectorAll('dialog')]
      this.handleClick = this.handleClick.bind(this)
      this.handleClose = this.handleClose.bind(this)

      this.addEventListener('click', this.handleClick)
      this.dialogs.forEach((dialog) => {
        dialog.addEventListener('close', this.handleClose)
      })
    }

    /**
     * Remove listeners when the section is removed in the theme editor.
     */
    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick)
      this.dialogs.forEach((dialog) => {
        dialog.removeEventListener('close', this.handleClose)
      })
    }

    /**
     * Open a dialog from a trigger, or close one from its backdrop.
     * @param {MouseEvent} event - Click event from inside the element.
     */
    handleClick(event) {
      if (event.target.tagName === 'DIALOG') {
        event.target.close()

        return
      }

      const trigger = event.target.closest('[js-incident="trigger"]')

      if (!trigger) {
        return
      }

      const dialog = this.querySelector(`dialog[id="${trigger.dataset.dialog}"]`)

      if (!dialog || dialog.open) {
        return
      }

      this.opener = trigger
      dialog.showModal()
    }

    /**
     * Return focus to the control that opened the dialog.
     */
    handleClose() {
      if (this.opener && this.opener.isConnected) {
        this.opener.focus()
      }

      this.opener = null
    }
  }

  customElements.define('tw-incident-map', TwIncidentMap)
}
